import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    Box,
    Typography,
    Card,
    CardContent,
    CircularProgress,
    useTheme,
    useMediaQuery,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
} from '@mui/material';
import { Business, CreditCard, Extension, TrendingUp, LocationCity, AccountBalance, Category, Subscriptions } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';

interface StatCardProps {
    title: string;
    value: number | string;
    icon: React.ReactElement;
    color: string;
    bgColor: string;
    onClick?: () => void;
}

const StatCard = ({ title, value, icon, color, bgColor, onClick }: StatCardProps) => {
    return (
        <Card
            sx={{
                height: '100%',
                cursor: onClick ? 'pointer' : 'default',
                transition: 'transform 0.2s',
                '&:hover': onClick ? { transform: 'translateY(-2px)' } : {},
            }}
            onClick={onClick}
        >
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                        <Typography color="text.secondary" variant="body2" gutterBottom>
                            {title}
                        </Typography>
                        <Typography variant="h4" fontWeight="bold">
                            {value}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            bgcolor: bgColor,
                            borderRadius: 2,
                            p: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: color,
                            '& svg': { fontSize: 32 },
                        }}
                    >
                        {icon}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

const Dashboard = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Fetch dashboard stats from API
    const { data, isLoading, error } = useQuery({
        queryKey: ['superadmin-dashboard'],
        queryFn: async () => {
            const res = await api.get('/superadmin/dashboard');
            if (res.data?.status === 'error') {
                throw new Error(res.data.message);
            }
            return res.data.data;
        },
    });

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                <CircularProgress />
            </Box>
        );
    }

    const statsData = data?.stats || {};
    const recentTenants = data?.recent_tenants || [];

    const stats = [
        {
            title: 'Total Tenants',
            value: statsData.total_tenants || 0,
            icon: <Business />,
            color: theme.palette.primary.main,
            bgColor: theme.palette.primary.light + '33',
            onClick: () => navigate('/super-admin/tenants'),
        },
        {
            title: 'Active Subscriptions',
            value: statsData.active_subscriptions || 0,
            icon: <Subscriptions />,
            color: theme.palette.success.main,
            bgColor: theme.palette.success.light + '33',
        },
        {
            title: 'Subscription Plans',
            value: statsData.total_plans || 0,
            icon: <CreditCard />,
            color: theme.palette.secondary.main,
            bgColor: theme.palette.secondary.light + '33',
            onClick: () => navigate('/super-admin/plans'),
        },
        {
            title: 'Addons',
            value: statsData.total_addons || 0,
            icon: <Extension />,
            color: theme.palette.info.main,
            bgColor: theme.palette.info.light + '33',
            onClick: () => navigate('/super-admin/addons'),
        },
        {
            title: 'Mandals',
            value: statsData.total_mandals || 0,
            icon: <LocationCity />,
            color: theme.palette.warning.main,
            bgColor: theme.palette.warning.light + '33',
            onClick: () => navigate('/super-admin/mandals'),
        },
        {
            title: 'Request Depts',
            value: statsData.total_complaint_departments || 0,
            icon: <AccountBalance />,
            color: '#9c27b0',
            bgColor: '#f3e5f5',
            onClick: () => navigate('/super-admin/complaint-departments'),
        },
        {
            title: 'Request Types',
            value: statsData.total_complaint_types || 0,
            icon: <Category />,
            color: '#00bcd4',
            bgColor: '#e0f7fa',
            onClick: () => navigate('/super-admin/complaint-types'),
        },
        {
            title: 'Total Revenue',
            value: `₹${(statsData.total_revenue || 0).toLocaleString()}`,
            icon: <TrendingUp />,
            color: theme.palette.success.dark,
            bgColor: theme.palette.success.light + '33',
        },
    ];

    return (
        <Box>
            <Typography variant="h4" gutterBottom>
                Dashboard
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 4 }}>
                Welcome to the Super Admin Panel
            </Typography>

            {/* Stats Grid */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                        md: 'repeat(4, 1fr)',
                    },
                    gap: 3,
                    mb: 4,
                }}
            >
                {stats.map((stat, index) => (
                    <StatCard key={index} {...stat} />
                ))}
            </Box>

            {/* Quick Actions */}
            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                Quick Actions
            </Typography>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                        md: 'repeat(3, 1fr)',
                    },
                    gap: 2,
                    mb: 4,
                }}
            >
                <Card
                    sx={{
                        cursor: 'pointer',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: theme.shadows[8],
                        },
                    }}
                    onClick={() => navigate('/super-admin/tenants')}
                >
                    <CardContent sx={{ textAlign: 'center', py: 3 }}>
                        <Business sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                        <Typography variant="h6">Manage Tenants</Typography>
                        <Typography color="text.secondary" variant="body2">
                            View and manage all tenants
                        </Typography>
                    </CardContent>
                </Card>

                <Card
                    sx={{
                        cursor: 'pointer',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: theme.shadows[8],
                        },
                    }}
                    onClick={() => navigate('/super-admin/plans')}
                >
                    <CardContent sx={{ textAlign: 'center', py: 3 }}>
                        <CreditCard sx={{ fontSize: 40, color: 'secondary.main', mb: 1 }} />
                        <Typography variant="h6">Manage Plans</Typography>
                        <Typography color="text.secondary" variant="body2">
                            Configure subscription plans
                        </Typography>
                    </CardContent>
                </Card>

                <Card
                    sx={{
                        cursor: 'pointer',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: theme.shadows[8],
                        },
                    }}
                    onClick={() => navigate('/super-admin/addons')}
                >
                    <CardContent sx={{ textAlign: 'center', py: 3 }}>
                        <Extension sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                        <Typography variant="h6">Manage Addons</Typography>
                        <Typography color="text.secondary" variant="body2">
                            Configure addon packages
                        </Typography>
                    </CardContent>
                </Card>
            </Box>

            {/* Recent Tenants */}
            {recentTenants.length > 0 && (
                <>
                    <Typography variant="h6" gutterBottom>
                        Recent Tenants
                    </Typography>
                    <Card>
                        <TableContainer>
                            <Table size={isMobile ? 'small' : 'medium'}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        {!isMobile && <TableCell>Email</TableCell>}
                                        <TableCell>Status</TableCell>
                                        <TableCell>Subscription</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {recentTenants.map((tenant: any) => (
                                        <TableRow key={tenant.id}>
                                            <TableCell>{tenant.name}</TableCell>
                                            {!isMobile && <TableCell>{tenant.email}</TableCell>}
                                            <TableCell>
                                                <Chip
                                                    label={tenant.is_active ? 'Active' : 'Inactive'}
                                                    size="small"
                                                    color={tenant.is_active ? 'success' : 'default'}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={tenant.has_subscription ? 'Subscribed' : 'No Plan'}
                                                    size="small"
                                                    color={tenant.has_subscription ? 'primary' : 'default'}
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Card>
                </>
            )}
        </Box>
    );
};

export default Dashboard;
